import React from "react";
import { Check } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { WEB_REGISTER_URL, formatNaira, formatUsd } from "@/constants/site";
import { usePublicPlans } from "@/hooks/usePublicPlans";
import { captureEvent } from "@/lib/analytics";

const INCLUDED = ["Sales, inventory & customer credit", "Offline-ready mobile selling", "Web and mobile access", "Business insights and reports"];

export const Pricing = () => {
  const { plans } = usePublicPlans();

  return (
    <section
      id="pricing"
      data-testid={LANDING.pricing}
      className="bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Pricing</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424] tracking-tight">
              Start with what fits today. <br />
              <span className="italic text-[#0E6B6B]">Move up when the business does.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#3a4a4a] max-w-lg">
              Every plan protects the same essential business record and includes monthly AI credits. Choose based on the branches, team members, and products you need.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={WEB_REGISTER_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={LANDING.ctaStartManaging}
                onClick={() => captureEvent("conversion_cta_clicked", { placement: "home_pricing", destination: "web_register" })}
                className="inline-flex items-center rounded-full bg-[#0E6B6B] px-6 py-3.5 text-base font-semibold text-white hover:bg-[#138282] transition-colors"
              >
                Start free
              </a>
              <a
                href="/pricing"
                data-testid={LANDING.ctaTalkSales}
                className="inline-flex items-center rounded-full border border-[#0E6B6B]/25 px-6 py-3.5 text-base font-semibold text-[#0E6B6B] hover:bg-[#0E6B6B]/5 transition-colors"
              >
                Compare all plans
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 grid gap-5 sm:grid-cols-2">
            {plans.slice(0, 2).map((plan, index) => (
              <div key={plan.code} className={`relative rounded-3xl border p-7 overflow-hidden ${index === 1 ? "bg-[#0A3B3B] border-[#0E6B6B] text-white" : "bg-[#F7FAF9] border-[#0E6B6B]/12 text-[#0A2424]"}`}>
                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#FFD8A8]/15 blur-3xl pointer-events-none" />
                {index === 1 ? <div className="rounded-full bg-[#FFD8A8] text-[#0A2424] px-3 py-1.5 text-xs font-bold inline-flex mb-5">For growing teams</div> : null}
                <div className={`text-xs uppercase tracking-widest font-bold ${index === 1 ? "text-[#FFD8A8]" : "text-[#D96C4A]"}`}>{plan.name}</div>
                <div className="mt-2 font-display text-4xl md:text-5xl font-semibold">{formatNaira(plan.priceMinor)}</div>
                <div className={`mt-1 text-sm ${index === 1 ? "text-white/70" : "text-[#4B5E5E]"}`}>{formatUsd(plan.usdPriceMinor)} international · per month</div>
                <div className={`mt-2 text-xs font-semibold ${index === 1 ? "text-[#FFD8A8]" : "text-[#0E6B6B]"}`}>{Number(plan.includedCredits || 0).toLocaleString()} AI credits included monthly</div>
                <p className={`mt-4 text-sm leading-relaxed ${index === 1 ? "text-white/75" : "text-[#4B5E5E]"}`}>{plan.description}</p>
                <ul className="mt-6 space-y-3">
                  {INCLUDED.map((item) => (
                    <li key={item} className={`flex items-start gap-3 text-[14px] ${index === 1 ? "text-white/90" : "text-[#1c2c2c]"}`}>
                      <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full ${index === 1 ? "bg-[#FFD8A8] text-[#0A2424]" : "bg-[#0E6B6B] text-white"}`}><Check className="h-3 w-3" strokeWidth={3} /></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
